'use client';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Button } from 'components/ui/button';
import { PlusCircle, Eye, Edit, Download } from 'lucide-react';
import { Badge } from 'components/ui/badge';
import type { Order } from '../lib/types';
import { useTranslation } from 'react-i18next';
import { getStatusColor, translateStatus } from '../lib/utils';
import { productUnitsMap } from '../lib/productUnits';
import { ReactNode, useState } from 'react';
import { exportOrdersToExcel } from '../lib/exportOrders';
import { toast } from 'sonner';

interface OrdersTableProps {
  orders: Order[];
  orgId?: string;
  onViewOrder?: (order: Order) => void;
  onEditOrder?: (order: Order) => void;
  onCreateOrder?: () => void;
  headerControls?: ReactNode;
}

export function OrdersTable({
  orders,
  orgId,
  onViewOrder,
  onEditOrder,
  onCreateOrder,
  headerControls,
}: OrdersTableProps) {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  // const { orders, loading } = useOrders();

  // The state management has been moved to OrderListContainer

  const handleExport = async () => {
    setIsExporting(true);

    try {
      await exportOrdersToExcel(orders);
      toast.success(`${orders.length} ordrer eksporteret til Excel`);
    } catch (error) {
      console.error('Could not export orders to Excel', error);
      toast.error('Ordre kunne ikke eksporteres til Excel');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <h2 className="text-xl font-semibold">Ordre</h2>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {headerControls}
          <Button
            variant="outline"
            disabled={orders.length === 0 || isExporting}
            onClick={handleExport}
          >
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Eksporterer...' : 'Eksporter Excel'}
          </Button>
          <Button onClick={onCreateOrder}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Ny ordre
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              {!orgId && <TableHead className="max-w-[240px]">Firma</TableHead>}
              <TableHead>Produker</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Oprettet</TableHead>
              <TableHead>Oprettet af</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                  Ingen ordrer fundet. Opret en ny ordre ved at klikke på "Ny ordre" knappen.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => {
                return (
                  <TableRow key={order.id}>
                    <TableCell className="font-medium">{order.id}</TableCell>
                    {!orgId && (
                      <TableCell className="max-w-[240px]">
                        <Button
                          variant="link"
                          className="text-blue-800 h-auto p-0 justify-start whitespace-normal text-left max-w-full"
                          onClick={() => window.open(`/orgs/${order.orgId}/orders`)}
                        >
                          <span
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {order.companyName}
                          </span>
                        </Button>
                      </TableCell>
                    )}
                    <TableCell>
                      {order.products?.map((product) => {
                        // @ts-ignore
                        const unit = productUnitsMap[product.id]?.find(
                          (p: any) => p.value == product.unit
                        );
                        console.log({ product, productUnitsMap, unit });
                        return (
                          <div key={product.id} className="flex space-x-1">
                            <div>
                              {
                                // @ts-ignore
                                order.isDeprecatedOrder
                                  ? product.quantity
                                  : product.quantity * unit?.value
                              }{' '}
                              {unit?.unit === 'l' ? 'liter' : 'stk'} -
                            </div>
                            <div>{product.name}</div>
                          </div>
                        );
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(order.status)}>
                        {t(translateStatus(order.status))}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {t('general.getDate', { date: order.createTime })}{' '}
                      {t('general.getMonth', { date: order.createTime })},{' '}
                      {t('general.getYear', { date: order.createTime })}
                    </TableCell>
                    <TableCell>{order.createdBy?.displayName}</TableCell>
                    <TableCell>
                      {
                        // @ts-ignore
                        !order.isDeprecatedOrder && (
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onViewOrder && onViewOrder(order)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onEditOrder && onEditOrder(order)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        )
                      }
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialogs have been moved to OrderListContainer */}
    </div>
  );
}
